"use client";
import HyperDX from "@hyperdx/browser";
import { isbot } from "isbot";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { createContext, ReactNode, useContext, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { isBotDetected } from "@/lib/analytics/bot-detection";

let hyperDXInitialized = false;
let postHogInitialized = false;

// Analytics SDKs can insert scripts into the document. Initialize them only
// after hydration starts, from effects or event handlers rather than module load.
function initializeHyperDX() {
  if (hyperDXInitialized || typeof window === "undefined") return;
  if (isBotDetected()) {
    // Log that we're skipping HyperDX for bots
    if (process.env.NODE_ENV === "development") {
      const reasons: string[] = [];
      const ua = navigator.userAgent;
      const automationSignatures = [
        "selenium",
        "webdriver",
        "puppeteer",
        "playwright",
        "cypress",
        "testcafe",
        "phantom",
        "headless",
      ];

      if (isbot(ua)) reasons.push("user-agent (isbot)");
      if (navigator.webdriver === true) reasons.push("webdriver property");
      if (ua.includes("HeadlessChrome") || ua.includes("PhantomJS"))
        reasons.push("headless browser");
      if (typeof navigator.permissions === "undefined" && ua.includes("Chrome"))
        reasons.push("missing permissions API");
      if (
        !Array.isArray(navigator.languages) ||
        navigator.languages.length === 0
      )
        reasons.push("missing languages");
      const matchedSig = automationSignatures.find((sig) =>
        ua.toLowerCase().includes(sig),
      );
      if (matchedSig) reasons.push(`automation signature (${matchedSig})`);
      if (
        typeof window.outerHeight === "undefined" ||
        typeof window.outerWidth === "undefined"
      )
        reasons.push("missing window properties");
      if (ua.length < 10 || ua === "Mozilla/5.0" || !ua.includes("Mozilla"))
        reasons.push("suspicious user agent");

      console.log(
        `[HyperDX] Skipping initialization for bot (detected via: ${reasons.join(", ") || "unknown"}):`,
        ua,
      );
    }
    return;
  }

  hyperDXInitialized = true;
  HyperDX.init({
    apiKey: process.env.NEXT_PUBLIC_HYPERDX_API_KEY as string,
    service: process.env.NEXT_PUBLIC_HYPERDX_SERVICE || "kautious-web",
    tracePropagationTargets: [
      /api\.kautious\.com/i,
      /localhost:3000/i,
      /vercel\.app/i,
    ],
    consoleCapture: true,
    advancedNetworkCapture: true,
    maskAllInputs: false,
    maskAllText: false,
    disableReplay: false,
  });

  // Attach HyperDX to React Error Boundary for automatic error capture
  HyperDX.attachToReactErrorBoundary(ErrorBoundary);

  // Debug: Log HyperDX initialization
  if (process.env.NODE_ENV === "development") {
    console.log(
      "[HyperDX] Initialized with service:",
      process.env.NEXT_PUBLIC_HYPERDX_SERVICE || "kautious-web",
    );
    console.log("[HyperDX] Session replay enabled:", true);
  }
}

// Initialize PostHog on the client side (analytics + session recording, skip for bots)
function initializePostHog() {
  if (
    postHogInitialized ||
    typeof window === "undefined" ||
    !process.env.NEXT_PUBLIC_POSTHOG_KEY ||
    isBotDetected()
  )
    return;

  postHogInitialized = true;
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    // Session recording enabled — runs alongside HyperDX session replay
    disable_session_recording: false,
    // Enable product analytics features
    capture_pageview: false, // We'll handle this manually
    capture_pageleave: true,
    // Performance and debugging
    loaded: (posthog: any) => {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[PostHog] Initialized for analytics (session recording enabled)",
        );
      }
    },
    // Privacy settings
    respect_dnt: true,
    opt_out_capturing_by_default: false,
  });
}

// Create a context for HyperDX functions
const HyperDXContext = createContext<{
  addAction: (action: string, metadata?: Record<string, any>) => void;
  setGlobalAttributes: (attributes: Record<string, any>) => void;
}>({
  addAction: () => {},
  setGlobalAttributes: () => {},
});

// Create a context for PostHog functions
const PostHogContext = createContext<{
  capture: (event: string, properties?: Record<string, any>) => void;
  identify: (distinctId: string, properties?: Record<string, any>) => void;
  setPersonProperties: (properties: Record<string, any>) => void;
}>({
  capture: () => {},
  identify: () => {},
  setPersonProperties: () => {},
});

export function HyperDXPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track pageviews
  useEffect(() => {
    if (pathname && typeof window !== "undefined") {
      initializeHyperDX();
      initializePostHog();
      let url = window.origin + pathname;
      if (searchParams?.toString()) {
        url = url + `?${searchParams.toString()}`;
      }

      // HyperDX pageview
      HyperDX.addAction("$pageview", {
        $current_url: url,
        pathname,
        search: searchParams?.toString(),
      });

      // PostHog pageview (if enabled)
      if (typeof posthog !== "undefined") {
        posthog.capture("$pageview", {
          $current_url: url,
          pathname,
          search: searchParams?.toString(),
        });
      }
    }
  }, [pathname, searchParams]);

  return null;
}

export function HyperDXProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    initializeHyperDX();
  }, []);

  const addAction = (action: string, metadata?: Record<string, any>) => {
    if (typeof window !== "undefined") {
      initializeHyperDX();
      HyperDX.addAction(action, metadata);
    }
  };

  const setGlobalAttributes = (attributes: Record<string, any>) => {
    if (typeof window !== "undefined") {
      initializeHyperDX();
      HyperDX.setGlobalAttributes(attributes);
    }
  };

  // Note: User identification is handled in AppFrame.tsx where we have access
  // to more detailed user information from Clerk

  return (
    <HyperDXContext.Provider value={{ addAction, setGlobalAttributes }}>
      {children}
    </HyperDXContext.Provider>
  );
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    initializePostHog();
  }, []);

  const capture = (event: string, properties?: Record<string, any>) => {
    if (typeof window !== "undefined" && typeof posthog !== "undefined") {
      initializePostHog();
      posthog.capture(event, properties);
    }
  };

  const identify = (distinctId: string, properties?: Record<string, any>) => {
    if (typeof window !== "undefined" && typeof posthog !== "undefined") {
      initializePostHog();
      posthog.identify(distinctId, properties);
    }
  };

  const setPersonProperties = (properties: Record<string, any>) => {
    if (typeof window !== "undefined" && typeof posthog !== "undefined") {
      initializePostHog();
      posthog.setPersonProperties(properties);
    }
  };

  return (
    <PostHogContext.Provider value={{ capture, identify, setPersonProperties }}>
      {children}
    </PostHogContext.Provider>
  );
}

export function useHyperDX() {
  return useContext(HyperDXContext);
}

export function usePostHog() {
  return useContext(PostHogContext);
}

// Legacy exports for backward compatibility during migration
export { HyperDXPageView as PostHogPageView, HyperDXProvider as PHProvider };
